import React from 'react';
import { View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import SecondaryButton from './SecondaryButton';

/**
 * EmptyState: empty, error or offline message with an optional action.
 * Props:
 * - title (string), message (string)
 * - icon: Feather icon name (default 'inbox'; error tone defaults to 'alert-circle'),
 *   or a node for another icon set
 * - tone: 'default' | 'error'
 * - actionLabel + onAction: e.g. "Try again" for errors
 * - style
 */
const EmptyState = ({
	title,
	message,
	icon,
	tone = 'default',
	actionLabel,
	onAction,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const isError = tone === 'error';
	const iconName = icon || (isError ? 'alert-circle' : 'inbox');

	return (
		<View style={[styles.container, style]} accessible={!onAction} accessibilityLabel={[title, message].filter(Boolean).join('. ')}>
			{typeof iconName === 'string'
				? <Feather name={iconName} size={34} color={isError ? colors.danger : colors.iconMuted} />
				: iconName}
			{Boolean(title) && <Text style={styles.title}>{title}</Text>}
			{Boolean(message) && <Text style={styles.message}>{message}</Text>}
			{Boolean(actionLabel && onAction) && (
				<SecondaryButton label={actionLabel} onPress={onAction} style={styles.action} />
			)}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	container: {
		alignItems: 'center',
		paddingVertical: space.xxl,
		paddingHorizontal: space.xl,
		gap: space.sm,
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
		textAlign: 'center',
		marginTop: space.xs,
	},
	message: {
		...type.secondary,
		color: colors.textSecondary,
		textAlign: 'center',
	},
	action: {
		marginTop: space.sm,
		minWidth: 160,
	},
});

export default EmptyState;
